import React from 'react';

export const BADGE_DEFINITIONS = [
  {
    id: 'AD-Enriched',
    label: 'AD-Enriched',
    className: 'specificity-badge--ad-enriched',
    description:
      'Matches the AD reference panel without competing aging, inflammation, or cross-neurodegeneration overlap in seed panels.',
  },
  {
    id: 'Aging Associated',
    label: 'Aging Associated',
    className: 'specificity-badge--aging-associated',
    description:
      'Overlaps aging-control reference panels; may reflect chronological or senescence-related biology rather than AD-specific signal.',
  },
  {
    id: 'General Inflammation Risk',
    label: 'General Inflammation Risk',
    className: 'specificity-badge--inflammation-risk',
    description:
      'Overlaps neuroinflammation reference panels; may reflect broad immune activation rather than AD-specific biology.',
  },
  {
    id: 'Cross-Neurodegeneration Shared',
    label: 'Cross-Neurodegeneration Shared',
    className: 'specificity-badge--cross-neurodegeneration',
    description:
      "Shared across other neurodegeneration seed panels (Parkinson's, ALS, or Huntington's); not uniquely Alzheimer's-specific.",
  },
];

const CROSS_NEURO_LABELS = {
  parkinsons: "Parkinson's",
  als: 'ALS',
  huntingtons: "Huntington's",
};

export const SpecificityBadges = ({ badges = [] }) => {
  const normalized = Array.isArray(badges) ? badges : [];
  const classFor = (badge) => {
    const match = BADGE_DEFINITIONS.find((item) => item.id === badge);
    return match?.className || 'status-badge';
  };

  if (!normalized.length) {
    return <span className="specificity-interpretation__muted">None</span>;
  }

  return (
    <div className="specificity-interpretation__badges">
      {normalized.map((badge) => (
        <span key={badge} className={`specificity-badge ${classFor(badge)}`}>
          {badge}
        </span>
      ))}
    </div>
  );
};

export const ScoreCell = ({ score }) => {
  const value = typeof score === 'number' && Number.isFinite(score) ? score : null;
  const text = value === null ? '—' : `${(value * 100).toFixed(1)}%`;
  return <span className="specificity-interpretation__score">{text}</span>;
};

export function resolveReferenceRelease(rows = []) {
  for (const row of rows) {
    const release = row?.specificity_evidence?.reference_release;
    if (release) return release;
  }
  return null;
}

export function computeSpecificitySummary(rows = []) {
  const counts = {
    'AD-Enriched': 0,
    'Aging Associated': 0,
    'General Inflammation Risk': 0,
    'Cross-Neurodegeneration Shared': 0,
  };

  rows.forEach((row) => {
    (row.specificity_badges || []).forEach((badge) => {
      if (Object.prototype.hasOwnProperty.call(counts, badge)) {
        counts[badge] += 1;
      }
    });
  });

  return {
    targetCount: rows.length,
    badgeCounts: counts,
    referenceRelease: resolveReferenceRelease(rows),
  };
}

function fallbackInterpretation(row) {
  const feature = row.feature || row.name || row.entity || 'This target';
  const badges = row.specificity_badges || [];
  const evidence = row.specificity_evidence || {};

  if (badges.includes('AD-Enriched')) {
    return `${feature} is likely Alzheimer's-specific based on seed AD panel overlap without competing panel signal.`;
  }
  if (evidence.in_ad_panel && (evidence.in_aging_panel || evidence.in_inflammation_panel)) {
    return `${feature} overlaps the AD reference panel but also matches other disease panels, so it should not be treated as uniquely AD-specific.`;
  }
  if (badges.includes('General Inflammation Risk')) {
    return `${feature} shows general inflammation risk and may reflect non-AD-specific immune biology.`;
  }
  if (badges.includes('Cross-Neurodegeneration Shared')) {
    return `${feature} is shared across other neurodegeneration seed panels and is not uniquely Alzheimer's-specific.`;
  }
  if (badges.includes('Aging Associated')) {
    return `${feature} is aging-associated relative to aging-control seed panels.`;
  }
  if (
    evidence.in_ad_panel ||
    evidence.in_aging_panel ||
    evidence.in_inflammation_panel ||
    Object.values(evidence.in_cross_neurodegeneration_panels || {}).some(Boolean)
  ) {
    return `${feature} matches one or more reference panels but did not meet badge thresholds for AD-enriched or competing categories.`;
  }
  return `${feature} did not match curated AD, aging, inflammation, or cross-neurodegeneration seed reference panels.`;
}

export const SpecificityInterpretationText = ({ row }) => {
  const text = row?.specificity_interpretation || fallbackInterpretation(row || {});
  return <p className="specificity-interpretation__text">{text}</p>;
};

export const SpecificityReferenceLine = ({ referenceRelease }) => {
  if (!referenceRelease) return null;
  return (
    <p className="specificity-interpretation__reference">
      Reference panels: <strong>{referenceRelease}</strong>
      <span className="specificity-interpretation__reference-note">
        {' '}
        (curated seed release for exploratory differentiation, not external cohort enrichment)
      </span>
    </p>
  );
};

export const SpecificityBadgeLegend = () => (
  <div className="specificity-interpretation__legend">
    <div className="specificity-interpretation__legend-title">Badge legend</div>
    <ul className="specificity-interpretation__legend-list">
      {BADGE_DEFINITIONS.map((badge) => (
        <li key={badge.id} className="specificity-interpretation__legend-item">
          <span className={`specificity-badge ${badge.className}`}>{badge.label}</span>
          <span>{badge.description}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const SpecificitySummaryBanner = ({ rows = [] }) => {
  if (!rows.length) return null;

  const { targetCount, badgeCounts, referenceRelease } = computeSpecificitySummary(rows);
  const bullets = [];

  if (badgeCounts['AD-Enriched']) {
    bullets.push(`${badgeCounts['AD-Enriched']} of ${targetCount} ranked feature(s) are AD-enriched`);
  }
  if (badgeCounts['General Inflammation Risk']) {
    bullets.push(`${badgeCounts['General Inflammation Risk']} show general inflammation risk`);
  }
  if (badgeCounts['Aging Associated']) {
    bullets.push(`${badgeCounts['Aging Associated']} are aging-associated`);
  }
  if (badgeCounts['Cross-Neurodegeneration Shared']) {
    bullets.push(`${badgeCounts['Cross-Neurodegeneration Shared']} overlap other neurodegeneration panels`);
  }
  if (!bullets.length) {
    bullets.push(`None of the ${targetCount} ranked feature(s) met seed-panel badge criteria`);
  }

  return (
    <div className="specificity-interpretation__summary">
      <div className="specificity-interpretation__summary-title">Target specificity summary</div>
      <ul className="specificity-interpretation__summary-list">
        {bullets.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <SpecificityReferenceLine referenceRelease={referenceRelease} />
    </div>
  );
};

export const StructuredEvidencePanel = ({ evidence }) => {
  if (!evidence || typeof evidence !== 'object') {
    return <span className="specificity-interpretation__muted">No panel evidence</span>;
  }

  const crossPanels = evidence.in_cross_neurodegeneration_panels || {};
  const crossMatches = Object.entries(crossPanels)
    .filter(([, matched]) => matched)
    .map(([key]) => CROSS_NEURO_LABELS[key] || key);

  const items = [
    { label: 'AD reference panel', matched: Boolean(evidence.in_ad_panel) },
    { label: 'Aging control panel', matched: Boolean(evidence.in_aging_panel) },
    { label: 'Inflammation panel', matched: Boolean(evidence.in_inflammation_panel) },
    {
      label: 'Cross-neurodegeneration panels',
      matched: crossMatches.length > 0,
      detail: crossMatches.length ? crossMatches.join(', ') : "Parkinson's, ALS, Huntington's — none matched",
    },
  ];

  return (
    <details className="specificity-interpretation__evidence">
      <summary className="specificity-interpretation__evidence-summary">View matched panels</summary>
      <ul className="specificity-interpretation__evidence-list">
        {items.map((item) => (
          <li key={item.label}>
            <span className="specificity-interpretation__evidence-label">{item.label}:</span>{' '}
            <span className={item.matched ? 'specificity-interpretation__evidence-yes' : 'specificity-interpretation__evidence-no'}>
              {item.matched ? 'Matched' : 'Not matched'}
            </span>
            {item.detail ? <span className="specificity-interpretation__evidence-detail"> ({item.detail})</span> : null}
          </li>
        ))}
        {evidence.reference_release ? (
          <li>
            <span className="specificity-interpretation__evidence-label">Reference release:</span>{' '}
            {evidence.reference_release}
          </li>
        ) : null}
      </ul>
    </details>
  );
};

export const SortableTableHeader = ({ label, column, sortBy, sortOrder, onSort, children }) => (
  <th
    className="specificity-interpretation__sortable-header"
    onClick={() => onSort(column)}
    aria-sort={sortBy === column ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
  >
    {children || label}
    <span className="specificity-interpretation__sort-icon" aria-hidden="true">
      {sortBy !== column ? '↕' : sortOrder === 'asc' ? '↑' : '↓'}
    </span>
  </th>
);

const UNIFIED_PATHWAY_COLUMNS = [
  { key: 'pathway_name', label: 'Pathway' },
  { key: 'combined_pathway_score', label: 'Combined score' },
  { key: 'consensus_count', label: 'Consensus' },
  { key: 'ad_specificity_score', label: 'AD specificity', score: true },
  { key: 'aging_association_score', label: 'Aging', score: true },
  { key: 'neuroinflammation_overlap_score', label: 'Inflammation', score: true },
  { key: 'cross_neurodegeneration_overlap_score', label: 'Cross-ND', score: true },
  { key: 'specificity_badges', label: 'Badges' },
  { key: 'specificity_interpretation', label: 'Interpretation' },
];

export const UnifiedPathwayTable = ({ rows = [] }) => {
  if (!rows.length) return <p>No pathways found.</p>;

  return (
    <div className="specificity-interpretation__pathway-table-wrap">
      <table className="specificity-interpretation__pathway-table">
        <thead>
          <tr>
            {UNIFIED_PATHWAY_COLUMNS.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${row.pathway_name || row.name || idx}`}>
              {UNIFIED_PATHWAY_COLUMNS.map((col) => {
                const value = row[col.key] ?? row.name;
                if (col.key === 'pathway_name') {
                  return (
                    <td key={col.key} className="specificity-interpretation__pathway-name">
                      {row.pathway_name || row.name || '—'}
                    </td>
                  );
                }
                if (col.score) {
                  return (
                    <td key={col.key}>
                      <ScoreCell score={value} />
                    </td>
                  );
                }
                if (col.key === 'specificity_badges') {
                  return (
                    <td key={col.key}>
                      <SpecificityBadges badges={value} />
                    </td>
                  );
                }
                if (col.key === 'specificity_interpretation') {
                  return (
                    <td key={col.key}>
                      <SpecificityInterpretationText row={row} />
                    </td>
                  );
                }
                if (col.key === 'combined_pathway_score' && typeof value === 'number') {
                  return <td key={col.key}>{value.toFixed(4)}</td>;
                }
                return <td key={col.key}>{value ?? '—'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
